// Provisioning Defaults — graphical editor (WS-D 2026-07-21 owner directive).
//
// The Dev Admin sees the BOOTSTRAP source tenant's live items per section as
// fully-visible selectable lists (select-all + per-item toggles, nothing
// clipped) — checked items constitute the defaults store payload the wizard
// provisions from. Password policy renders as real form fields. A secondary
// "advanced" raw-JSON view remains per section. Editing here writes ONLY the
// store; provisioning never live-reads the source tenant.
import { useState, useEffect, useCallback } from "react";
import { T } from "../theme";
import { apiCall } from "../apiClient";
import { Btn, Badge } from "./primitives";

interface TenantOpt { tenant_id: string; name: string; status?: string }

const SECTION_LABELS: Record<string, string> = {
  roles: "Roles & Permissions",
  contract_types: "Contract Types",
  routing_rules: "Routing Rules",
  draft_upload_rules: "Draft Upload Rules",
  sla_targets: "SLA Targets",
  parent_requirements: "Parent Requirements",
  extraction_profiles: "Extraction Profiles",
  business_units: "Business Units",
  prompt_templates: "Prompt Templates",
  password_policy: "Password Policy",
};

const keyOf = (section: string, it: any): string => {
  switch (section) {
    case "roles": return String(it.slug);
    case "contract_types": return `${it.category}|${it.name}`;
    case "routing_rules": return String(it.agreement_type);
    case "draft_upload_rules": return String(it.draft_label);
    case "sla_targets": return String(it.agreement_type);
    case "parent_requirements": return `${it.agreement_category}|${it.child_type}`;
    case "extraction_profiles": return String(it.name);
    case "business_units": return String(it.name);
    case "prompt_templates": return String(it.name);
    default: return JSON.stringify(it);
  }
};

const itemLabel = (section: string, it: any) => {
  switch (section) {
    case "roles": return <><strong>{it.display_name || it.slug}</strong> <span style={{ color: T.txD, fontSize: 11 }}>({it.slug} · {(it.permissions || []).length} permissions)</span></>;
    case "contract_types": return <><strong>{it.name}</strong> {it.short_code && <Badge c={T.txM} b={T.bg4}>{it.short_code}</Badge>} {it.is_amendment_style && <Badge c={T.orn} b={T.ornS}>amendment-style</Badge>}</>;
    case "routing_rules": return <><strong>{it.agreement_type}</strong> <span style={{ color: T.txD, fontSize: 11 }}>→ {it.primary_route}/{it.primary_role}{it.secondary_route ? ` · 2nd: ${it.secondary_route}` : ""}</span></>;
    case "draft_upload_rules": return <><strong>{it.draft_label}</strong> <span style={{ color: T.txD, fontSize: 11 }}>→ {it.target_status}{it.routing_aware ? " (routing-aware)" : ""}</span></>;
    case "sla_targets": return <><strong>{it.agreement_type || "(all types)"}</strong> <span style={{ color: T.txD, fontSize: 11 }}>{it.target_days} days · {it.severity}</span></>;
    case "parent_requirements": return <><strong>{it.agreement_category} / {it.child_type}</strong> <span style={{ color: T.txD, fontSize: 11 }}>parents: {(it.required_parent_types || []).join(", ")}</span></>;
    // (parent rows get kept/dropped parent styling in the section renderer)
    case "extraction_profiles": return <><strong>{it.name}</strong> <span style={{ color: T.txD, fontSize: 11 }}>{(it.fields || []).length} fields{it.is_system ? " · SYSTEM" : ""}</span></>;
    case "business_units": return <><strong>{it.name}</strong> {it.parent_name && <span style={{ color: T.txD, fontSize: 11 }}>↳ under {it.parent_name}</span>}</>;
    case "prompt_templates": return <><strong>{it.name}</strong> {it.category && <Badge c={T.blu} b={T.bluS}>{it.category}</Badge>} {it.is_global && <Badge c={T.txM} b={T.bg4}>global</Badge>} {it.inject_as_system && <span style={{ color: T.txD, fontSize: 10 }}>system-inject</span>}</>;
    default: return <span>{JSON.stringify(it).slice(0, 80)}</span>;
  }
};

export const DefaultsEditor = ({ tenants }: { tenants: TenantOpt[] }) => {
  const [store, setStore] = useState<Record<string, any>>({});
  const [meta, setMeta] = useState<Record<string, any>>({});
  // contract-type name → extraction-profile name (server-computed with the
  // real matcher; the cascade must never reimplement the matching here).
  const [typeMap, setTypeMap] = useState<Record<string, string | null>>({});
  // referenced type-name → CANDIDATE catalog type names (server-computed by the
  // shared conservative resolver in utils/typeMatch.js — the client only
  // intersects candidates with the live selection, never re-matches).
  const [matchMap, setMatchMap] = useState<Record<string, string[]>>({});
  // Inline full-routing edits (owner 2026-07-22: EVERY active rule is
  // editable — roles, routes, and the type for needs-a-type rules). Persisted
  // into the resolved payload on Save.
  const [ruleEdits, setRuleEdits] = useState<Record<string, any>>({});
  const [editingRule, setEditingRule] = useState<string | null>(null);
  const [selected, setSelected] = useState<Record<string, Set<string>>>({});
  const [policy, setPolicy] = useState({ min_length: 8, require_upper: false, require_digit: false, require_special: false });
  const [raw, setRaw] = useState<Record<string, boolean>>({});
  const [rawText, setRawText] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true); setErr("");
    const d = await apiCall("/platform/provisioning-defaults");
    if (!d?.sections) { setErr("Could not load the defaults store."); setLoading(false); return; }
    let mergedMap: Record<string, string | null> = { ...(d.type_profile_map || {}) };
    let mergedMatch: Record<string, string[]> = { ...(d.type_match_map || {}) };
    const st: Record<string, any> = {}; const mt: Record<string, any> = {};
    for (const s of d.sections) { st[s.section] = Array.isArray(s.payload) ? s.payload : []; mt[s.section] = s; }
    setStore(st); setMeta(mt);
    setTypeMap(mergedMap);
    setMatchMap(mergedMatch);
    setRuleEdits({}); setEditingRule(null);
    // CATALOG + SELECTION model (owner 2026-07-22): the store payload IS the
    // full Bootstrap catalog; each item's `selected` flag is the selection
    // layer. Checked = selected !== false. Deselecting NEVER removes an item.
    const sel: Record<string, Set<string>> = {};
    for (const section of Object.keys(SECTION_LABELS)) {
      if (section === "password_policy") continue;
      sel[section] = new Set((st[section] || []).filter((it: any) => it.selected !== false).map((it: any) => keyOf(section, it)));
    }
    setSelected(sel);
    const p = (st.password_policy && st.password_policy[0]) || null;
    if (p) setPolicy({ min_length: p.min_length ?? 8, require_upper: !!p.require_upper, require_digit: !!p.require_digit, require_special: !!p.require_special });
    setRawText(Object.fromEntries(Object.keys(st).map(k => [k, JSON.stringify(st[k], null, 2)])));
    setLoading(false);
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  // The stored payload IS the full catalog — render it verbatim.
  const itemsFor = (section: string): { it: any; k: string; inSource: boolean }[] =>
    (store[section] || []).map((it: any) => ({ it, k: keyOf(section, it), inSource: true }));

  const toggle = (section: string, k: string) => setSelected(p => {
    const s = new Set(p[section] || []);
    if (s.has(k)) s.delete(k); else s.add(k);
    return { ...p, [section]: s };
  });
  const setAll = (section: string, keys: string[], on: boolean) => setSelected(p => {
    const s = new Set(p[section] || []);
    keys.forEach(k => on ? s.add(k) : s.delete(k));
    return { ...p, [section]: s };
  });

  // ── Dependency cascade (owner 2026-07-22) ─────────────────────────────────
  // Derived live from the CURRENT toggles — nothing stored, so re-selecting a
  // type/role instantly restores the prior state of its dependents.
  // Type → dependents: routing_rules.agreement_type (name), parent rules
  // (child_type in its category + required_parent_types names), extraction
  // profile via the server-provided name-matcher map, sla_targets. Prompt
  // templates carry NO type binding (schema+code verified) — excluded.
  // Role → consumers: routing primary/secondary roles (strip, keep rule, flag
  // when roleless) and draft_upload_rules.authorized_roles (strip; empty means
  // "any role" by that table's semantics, so no flag needed).
  const selCT = selected["contract_types"] || new Set<string>();
  const selRoles = selected["roles"] || new Set<string>();
  const selectedTypeNames = new Set(Array.from(selCT).map(k => String(k.split("|")[1] || "").toLowerCase()));
  const typeNameOn = (name: any) => selectedTypeNames.has(String(name || "").toLowerCase());
  const selectedTypeNamesDisplay = new Set(Array.from(selCT).map(k => String(k.split("|")[1] || "")).filter(Boolean));
  // Fuzzy type-reference resolution (owner 2026-07-22): candidates come from
  // the SHARED server resolver; unambiguous → follow that type's selection;
  // ambiguous with kept candidates → NOT grayed, "needs a type" (owner picks);
  // all candidates deselected → correctly grayed.
  const typeRef = (name: any, editKey?: string): { status: "ok" | "gray" | "needs_type"; resolved?: string; options?: string[]; reason?: string } => {
    const picked = editKey ? ruleEdits[editKey]?.agreement_type : undefined;
    if (picked && typeNameOn(picked)) return { status: "ok", resolved: picked };
    if (typeNameOn(name)) return { status: "ok", resolved: String(name) };
    const candidates = matchMap[String(name)] ?? [];
    const kept = candidates.filter(c => typeNameOn(c));
    if (candidates.length === 1) {
      return kept.length ? { status: "ok", resolved: candidates[0] } : { status: "gray", reason: `type "${candidates[0]}" deselected` };
    }
    if (candidates.length === 0) return { status: "needs_type", options: [] };
    if (kept.length === 0) return { status: "gray", reason: "all candidate types deselected" };
    return { status: "needs_type", options: kept };
  };
  const grayReason = (section: string, it: any): string | null => {
    if (section === "routing_rules") {
      const r = typeRef(it.agreement_type, keyOf(section, it));
      return r.status === "gray" ? (r.reason || "type deselected") : null;
    }
    if (section === "parent_requirements") {
      const child = typeRef(it.child_type);
      if (child.status === "gray") return child.reason || `child type "${it.child_type}" deselected`;
      const keptParents = (it.required_parent_types || []).filter((pt: string) => typeRef(pt).status === "ok");
      // A rule survives while its child and AT LEAST ONE parent option remain
      // (the requirement just offers fewer parents) — only an empty parent set
      // grays it. Fixes the must-keep Amendment rules.
      return keptParents.length === 0 ? "all parent types deselected" : null;
    }
    if (section === "extraction_profiles") {
      const mappedBy = Object.keys(typeMap).filter(n => typeMap[n] === it.name);
      if (mappedBy.length === 0) return null; // no type maps here — not type-bound, never grayed
      return mappedBy.some(n => typeNameOn(n)) ? null : "all matching types deselected";
    }
    if (section === "sla_targets" && it.agreement_type) return typeNameOn(it.agreement_type) ? null : `type "${it.agreement_type}" deselected`;
    return null;
  };
  const effRoles = (it: any) => ({
    primary: it.primary_role && selRoles.has(it.primary_role) ? it.primary_role : null,
    secondary: it.secondary_role && selRoles.has(it.secondary_role) ? it.secondary_role : null,
  });
  const effAuthorized = (csv: any): string | null => {
    if (csv === null || csv === undefined || String(csv).trim() === "") return null;
    const kept = String(csv).split(",").map(x => x.trim()).filter(x => x && selRoles.has(x));
    return kept.length ? kept.join(",") : null;
  };
  const resolveItem = (section: string, it: any) => {
    if (section === "routing_rules") {
      const k = keyOf(section, it);
      const merged = { ...it, ...(ruleEdits[k] || {}) };
      const e = effRoles(merged);
      const tr = typeRef(it.agreement_type, k);
      return { ...merged, primary_role: e.primary, secondary_role: e.secondary, agreement_type: tr.resolved || merged.agreement_type };
    }
    if (section === "parent_requirements") {
      const parents = (it.required_parent_types || [])
        .map((pt: string) => typeRef(pt))
        .filter((r: any) => r.status === "ok")
        .map((r: any) => r.resolved);
      const child = typeRef(it.child_type);
      return { ...it, child_type: child.resolved || it.child_type, required_parent_types: Array.from(new Set(parents)) };
    }
    if (section === "draft_upload_rules") return { ...it, authorized_roles: effAuthorized(it.authorized_roles) };
    return it;
  };

  // Business-unit catalog edits (owner 2026-07-22: BUs are an editable
  // default). Add/Remove are EXPLICIT catalog edits (unlike deselection, which
  // never prunes); they mutate the local catalog and persist on Save.
  const [newBu, setNewBu] = useState("");
  const addBu = () => {
    const name = newBu.trim();
    if (!name) return;
    if ((store.business_units || []).some((b: any) => String(b.name).toLowerCase() === name.toLowerCase())) { setErr(`Business unit "${name}" already exists.`); return; }
    setStore(p => ({ ...p, business_units: [...(p.business_units || []), { name, is_active: true, selected: true }] }));
    setSelected(p => ({ ...p, business_units: new Set([...Array.from(p.business_units || []), name]) }));
    setNewBu("");
  };
  const removeBu = (name: string) => {
    // Remove the unit and detach any children referencing it as parent.
    setStore(p => ({ ...p, business_units: (p.business_units || []).filter((b: any) => b.name !== name).map((b: any) => b.parent_name === name ? { ...b, parent_name: null } : b) }));
    setSelected(p => { const st = new Set(p.business_units || []); st.delete(name); return { ...p, business_units: st }; });
  };

  const save = async (section: string) => {
    setBusy(section); setErr(""); setMsg("");
    let payload: any[];
    if (section === "password_policy") {
      payload = [{ min_length: Math.max(4, Number(policy.min_length) || 8), require_upper: policy.require_upper, require_digit: policy.require_digit, require_special: policy.require_special }];
    } else if (raw[section]) {
      try { payload = JSON.parse(rawText[section] || "[]"); } catch { setBusy(null); setErr(`${section}: not valid JSON.`); return; }
      if (!Array.isArray(payload)) { setBusy(null); setErr(`${section}: payload must be a JSON array.`); return; }
    } else {
      const sel = selected[section] || new Set();
      // CATALOG + SELECTION (owner 2026-07-22): Save writes the FULL catalog —
      // every item, resolved (role-strips/edits/name-rewrites applied) — with
      // its `selected` flag from the checkbox state. Selection NEVER prunes
      // the catalog; only an explicit re-seed repopulates it.
      payload = itemsFor(section)
        .map(x => ({ ...resolveItem(section, x.it), selected: sel.has(x.k) }));
    }
    const r = await apiCall(`/platform/provisioning-defaults/${section}`, { method: "PUT", body: JSON.stringify({ payload }) });
    setBusy(null);
    if (!r) { setErr(`${SECTION_LABELS[section]}: save failed.`); return; }
    setStore(p => ({ ...p, [section]: payload }));
    setRawText(p => ({ ...p, [section]: JSON.stringify(payload, null, 2) }));
    setMsg(`${SECTION_LABELS[section]} saved (${payload.length} item${payload.length === 1 ? "" : "s"}). Applies to tenants provisioned from now on.`);
  };

  const sectionBlock = (section: string) => {
    const m = meta[section] || {};
    if (section === "password_policy") {
      return (
        <div key={section} style={{ marginBottom: 16, border: `1px solid ${T.brd}`, borderRadius: 8, padding: "12px 14px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div><span style={{ fontSize: 14, fontWeight: 700, color: T.tx }}>{SECTION_LABELS[section]}</span>
              <span style={{ fontSize: 11, color: T.txD, marginLeft: 8 }}>seeded from Bootstrap · provisioned to every wizard tenant</span></div>
            <Btn v="secondary" sz="sm" onClick={() => save(section)} disabled={busy === section}>{busy === section ? "Saving…" : "Save"}</Btn>
          </div>
          <div style={{ display: "flex", gap: 22, alignItems: "center", flexWrap: "wrap" }}>
            <label style={{ fontSize: 13, color: T.tx, display: "inline-flex", gap: 8, alignItems: "center" }}>
              Minimum length
              <input type="number" min={4} max={128} value={policy.min_length}
                onChange={e => setPolicy(p => ({ ...p, min_length: parseInt(e.target.value) || 8 }))}
                style={{ width: 70, padding: "5px 8px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx }} />
            </label>
            {([["require_upper", "Require uppercase"], ["require_digit", "Require digit"], ["require_special", "Require special character"]] as [keyof typeof policy, string][]).map(([k, label]) => (
              <label key={k} style={{ fontSize: 13, color: T.tx, display: "inline-flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
                <input type="checkbox" checked={!!policy[k]} onChange={e => setPolicy(p => ({ ...p, [k]: e.target.checked }))} /> {label}
              </label>
            ))}
          </div>
        </div>
      );
    }
    const items = itemsFor(section);
    const sel = selected[section] || new Set();
    const availItems = items.filter(x => !grayReason(section, x.it));
    const allKeys = availItems.map(x => x.k);
    const effCount = availItems.filter(x => sel.has(x.k)).length;
    const grouped = section === "contract_types"
      ? Array.from(new Set(items.map(x => x.it.category))).map(cat => ({ cat, rows: items.filter(x => x.it.category === cat) }))
      : [{ cat: null as string | null, rows: items }];
    return (
      <div key={section} style={{ marginBottom: 16, border: `1px solid ${T.brd}`, borderRadius: 8, padding: "12px 14px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, gap: 8, flexWrap: "wrap" }}>
          <div>
            <span style={{ fontSize: 14, fontWeight: 700, color: T.tx }}>{SECTION_LABELS[section]}</span>
            <span style={{ fontSize: 11, color: T.txD, marginLeft: 8 }}>
              {effCount}/{availItems.length} selected{items.length > availItems.length ? ` · ${items.length - availItems.length} unavailable` : ""}
              {m.seeded_at ? ` · captured ${new Date(m.seeded_at).toLocaleString()}` : " · not seeded"}
            </span>
          </div>
          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
            <Btn v="ghost" sz="sm" onClick={() => setAll(section, allKeys, effCount < availItems.length)}>{effCount < availItems.length ? "Select all" : "Clear all"}</Btn>
            <Btn v="ghost" sz="sm" onClick={() => setRaw(p => ({ ...p, [section]: !p[section] }))}>{raw[section] ? "List view" : "Advanced"}</Btn>
            <Btn v="secondary" sz="sm" onClick={() => save(section)} disabled={busy === section}>{busy === section ? "Saving…" : "Save"}</Btn>
          </div>
        </div>
        {section === "business_units" && !raw[section] && (
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
            <input value={newBu} onChange={e => setNewBu(e.target.value)} placeholder="New business unit name"
              onKeyDown={e => { if (e.key === "Enter") addBu(); }}
              style={{ flex: 1, maxWidth: 280, padding: "6px 9px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12.5 }} />
            <Btn v="secondary" sz="sm" onClick={addBu} disabled={!newBu.trim()}>+ Add unit</Btn>
            <span style={{ fontSize: 11, color: T.txD }}>Add/Remove edit the catalog itself (Save to persist); checkboxes only select.</span>
          </div>
        )}
        {raw[section] ? (
          <textarea value={rawText[section] ?? ""} onChange={e => setRawText(p => ({ ...p, [section]: e.target.value }))} spellCheck={false}
            style={{ width: "100%", minHeight: 180, fontFamily: "monospace", fontSize: 11.5, padding: 8, borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, boxSizing: "border-box", resize: "vertical" }} />
        ) : items.length === 0 ? (
          <div style={{ fontSize: 12, color: T.txM }}>No items in the Bootstrap source{section === "sla_targets" ? " (none configured anywhere yet — empty is correct)" : ""}.</div>
        ) : grouped.map(g => (
          <div key={g.cat ?? "_"} style={{ marginBottom: g.cat ? 8 : 0 }}>
            {g.cat && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "6px 0 4px" }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: T.txD, textTransform: "uppercase", letterSpacing: ".04em" }}>{g.cat} ({g.rows.filter(x => sel.has(x.k)).length}/{g.rows.length})</span>
                <Btn v="ghost" sz="sm" onClick={() => setAll(section, g.rows.map(x => x.k), g.rows.some(x => !sel.has(x.k)))}>{g.rows.some(x => !sel.has(x.k)) ? "all" : "none"}</Btn>
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: section === "prompt_templates" || section === "parent_requirements" || section === "routing_rules" ? "1fr" : "1fr 1fr", gap: "4px 14px", minWidth: 0 }}>
              {g.rows.map(({ it, k, inSource }) => {
                const reason = grayReason(section, it);
                const eff = section === "routing_rules" ? effRoles({ ...it, ...(ruleEdits[k] || {}) }) : null;
                const needsRole = !!eff && !eff.primary && !eff.secondary;
                return (
                  <label key={k} style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 13, color: T.tx, cursor: reason ? "not-allowed" : "pointer", padding: "3px 2px", overflowWrap: "anywhere", opacity: reason ? 0.45 : 1, minWidth: 0 }}>
                    <input type="checkbox" checked={!reason && sel.has(k)} disabled={!!reason} onChange={() => toggle(section, k)} style={{ marginTop: 3 }} />
                    <span style={{ minWidth: 0, flex: 1 }}>
                      {section === "routing_rules" && eff ? (() => {
                        const merged = { ...it, ...(ruleEdits[k] || {}) };
                        const tr = typeRef(it.agreement_type, k);
                        const isEditing = editingRule === k;
                        const setEdit = (patch: any) => setRuleEdits(prev => ({ ...prev, [k]: { ...(prev[k] || {}), ...patch } }));
                        return (
                        <>
                          <strong>{tr.resolved || it.agreement_type}</strong>
                          {tr.resolved && tr.resolved !== it.agreement_type && <span style={{ color: T.txD, fontSize: 10, marginLeft: 4 }} title={`auto-resolved from "${it.agreement_type}"`}>(was: {it.agreement_type})</span>}{" "}
                          <span style={{ color: T.txD, fontSize: 11 }}>
                            → {merged.primary_route}/
                            {merged.primary_role && (eff.primary === merged.primary_role
                              ? <span>{merged.primary_role}</span>
                              : <s title="role deselected — stripped from this rule">{merged.primary_role}</s>)}
                            {merged.secondary_role && <>{" · 2nd: "}{eff.secondary === merged.secondary_role
                              ? <span>{merged.secondary_role}</span>
                              : <s title="role deselected — stripped from this rule">{merged.secondary_role}</s>}</>}
                          </span>
                          {needsRole && !reason && <Badge c={T.orn} b={T.ornS}>needs a role</Badge>}
                          {tr.status === "needs_type" && !reason && <Badge c={T.orn} b={T.ornS}>needs a type</Badge>}
                          {!reason && <Btn v="ghost" sz="sm" onClick={() => setEditingRule(isEditing ? null : k)}>{isEditing ? "Close" : "✎ Edit"}</Btn>}
                          {isEditing && !reason && (
                            <span onClick={e => { e.preventDefault(); e.stopPropagation(); }} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 6, padding: "8px 10px", borderRadius: 6, border: `1px dashed ${T.brd}`, backgroundColor: T.bg2 }}>
                              {tr.status === "needs_type" && (
                                <label style={{ fontSize: 11, color: T.txM }}>Type{" "}
                                  <select value={ruleEdits[k]?.agreement_type || ""} onChange={e => setEdit({ agreement_type: e.target.value })}
                                    style={{ padding: "3px 6px", borderRadius: 5, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 11 }}>
                                    <option value="">— pick a kept type —</option>
                                    {(tr.options && tr.options.length ? tr.options : Array.from(selectedTypeNamesDisplay)).map(t => <option key={t} value={t}>{t}</option>)}
                                  </select>
                                </label>
                              )}
                              <label style={{ fontSize: 11, color: T.txM }}>Primary route{" "}
                                <select value={merged.primary_route || "legal_ops"} onChange={e => setEdit({ primary_route: e.target.value })}
                                  style={{ padding: "3px 6px", borderRadius: 5, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 11 }}>
                                  {Array.from(new Set(["legal_ops", "commercial_ops", merged.primary_route].filter(Boolean))).map(rt => <option key={rt} value={rt}>{rt}</option>)}
                                </select>
                              </label>
                              <label style={{ fontSize: 11, color: T.txM }}>Primary role{" "}
                                <select value={merged.primary_role || ""} onChange={e => setEdit({ primary_role: e.target.value || null })}
                                  style={{ padding: "3px 6px", borderRadius: 5, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 11 }}>
                                  <option value="">—</option>
                                  {Array.from(selRoles).map(r => <option key={r} value={r}>{r}</option>)}
                                </select>
                              </label>
                              <label style={{ fontSize: 11, color: T.txM }}>Secondary route{" "}
                                <select value={merged.secondary_route || ""} onChange={e => setEdit({ secondary_route: e.target.value || null })}
                                  style={{ padding: "3px 6px", borderRadius: 5, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 11 }}>
                                  <option value="">none</option>
                                  {Array.from(new Set(["legal_ops", "commercial_ops", merged.secondary_route].filter(Boolean))).map(rt => <option key={rt} value={rt}>{rt}</option>)}
                                </select>
                              </label>
                              <label style={{ fontSize: 11, color: T.txM }}>Secondary role{" "}
                                <select value={merged.secondary_role || ""} onChange={e => setEdit({ secondary_role: e.target.value || null })}
                                  style={{ padding: "3px 6px", borderRadius: 5, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 11 }}>
                                  <option value="">none</option>
                                  {Array.from(selRoles).map(r => <option key={r} value={r}>{r}</option>)}
                                </select>
                              </label>
                              <Btn v="secondary" sz="sm" onClick={() => setEditingRule(null)}>Done</Btn>
                            </span>
                          )}
                        </>
                        );
                      })() : section === "draft_upload_rules" ? (
                        <>
                          <strong>{it.draft_label}</strong>{" "}
                          <span style={{ color: T.txD, fontSize: 11 }}>→ {it.target_status}{it.routing_aware ? " (routing-aware)" : ""} · roles: {effAuthorized(it.authorized_roles) || "any"}</span>
                        </>
                      ) : itemLabel(section, it)}
                      {reason && <span style={{ color: T.txD, fontSize: 10, marginLeft: 6, fontStyle: "italic" }}>unavailable — {reason}</span>}
                      {section === "business_units" && <Btn v="ghost" sz="sm" onClick={() => removeBu(it.name)} style={{ color: T.red, marginLeft: 6 }}>✕ Remove</Btn>}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      <p style={{ fontSize: 12.5, color: T.txM, margin: "0 0 10px" }}>
        The full <strong>Bootstrap Dev Tenant</strong> catalog is always shown — checking/unchecking is a selection layer
        that never removes an item from the menu. Checked items form the
        defaults new tenants receive as their <strong>own independent copies</strong> — edits apply to tenants provisioned after the save; provisioning
        never live-reads Bootstrap. Enter-fresh items (SharePoint, SSO, entities) are configured per tenant, not here.
      </p>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12, padding: "8px 10px", border: `1px dashed ${T.brd}`, borderRadius: 8 }}>
        <span style={{ fontSize: 12, color: T.txM, fontWeight: 600 }}>Source:</span>
        <span style={{ fontSize: 12, color: T.tx }}>Bootstrap Dev Tenant <span style={{ color: T.txD }}>(fixed — the single defaults source)</span></span>
        <Btn v="secondary" sz="sm" onClick={async () => {
          setErr(""); setMsg(""); setBusy("__reseed__");
          const r = await apiCall("/platform/provisioning-defaults/reseed", { method: "POST", body: JSON.stringify({ force: true }) });
          setBusy(null);
          if (!r?.results) { setErr("Re-seed failed."); return; }
          setMsg(`Catalog re-seeded from Bootstrap: ` + r.results.map((x: any) => `${x.section}=${x.count}`).join(", ") + ". Selections preserved.");
          await loadAll();
        }} disabled={busy === "__reseed__"}>{busy === "__reseed__" ? "Re-seeding…" : "Re-seed catalog from Bootstrap"}</Btn>
      </div>
      {err && <div style={{ marginBottom: 10, padding: "8px 12px", borderRadius: 6, backgroundColor: T.redS, color: T.red, fontSize: 12.5, border: `1px solid ${T.red}` }}>{err}</div>}
      {msg && <div style={{ marginBottom: 10, padding: "8px 12px", borderRadius: 6, backgroundColor: T.grnS, color: T.grn, fontSize: 12.5, border: `1px solid ${T.grn}` }}>{msg}</div>}
      {loading ? <div style={{ fontSize: 13, color: T.txM }}>Loading…</div> : Object.keys(SECTION_LABELS).map(sectionBlock)}
    </div>
  );
};
